import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from '@design-system';
import { ArrowLeft, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import {
  createNewSection,
  createNewSubsection,
  useFormEditorSidebar,
  useFormMetadata,
  useFormDefinition,
  useCurrentSectionIndex,
  useCurrentSubsectionIndex,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';

interface FormEditorSidebarProps {
  onBack: () => void;
}

export function FormEditorSidebar({ onBack }: FormEditorSidebarProps) {
  const formMetadata = useFormMetadata();
  const formDefinition = useFormDefinition();
  const currentSectionIndex = useCurrentSectionIndex();
  const currentSubsectionIndex = useCurrentSubsectionIndex();
  const { navigateTo, addSection, addSubsection } = getFormBuilderActions();
  const { isExpanded, toggleSection } = useFormEditorSidebar();

  return (
    <div className="sticky top-0 hidden h-screen w-80 shrink-0 overflow-y-auto border-r bg-white shadow-sm md:block">
      <div className="p-6">
        <div className="mb-6 flex items-center gap-4 p-1">
          <Button
            variant="secondary"
            onClick={onBack}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h2 className="text-base font-semibold text-gray-950">
              {formMetadata.title || 'Untitled Form'}
            </h2>
            {formMetadata.product && (
              <p className="text-muted-foreground text-xs">{formMetadata.product}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {formDefinition.sections.map((section, sectionIndex) => (
            <Collapsible
              key={section.id}
              open={isExpanded(sectionIndex)}
              onOpenChange={() => toggleSection(sectionIndex)}
            >
              <CollapsibleTrigger asChild>
                <div
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors',
                    sectionIndex === currentSectionIndex
                      ? 'border-primary/10 bg-primary/5 border'
                      : 'hover:bg-muted',
                  )}
                  onClick={() => {
                    navigateTo(sectionIndex, 0);
                  }}
                >
                  <div
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                      sectionIndex === currentSectionIndex
                        ? 'border-primary text-primary border bg-white'
                        : 'bg-primary/5 text-foreground',
                    )}
                  >
                    {sectionIndex + 1}
                  </div>
                  <span
                    className={cn(
                      'flex-1 text-left text-sm font-medium',
                      sectionIndex === currentSectionIndex ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {section.title || 'Untitled Section'}
                  </span>
                  {isExpanded(sectionIndex) ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
                  )}
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent className="ml-9 mt-2 space-y-1">
                {section.subsections.map((subsection, subsectionIndex) => (
                  <div
                    key={subsection.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm transition-colors',
                      sectionIndex === currentSectionIndex &&
                        subsectionIndex === currentSubsectionIndex
                        ? 'bg-primary/5 text-primary'
                        : 'text-foreground hover:bg-muted',
                    )}
                    onClick={() => navigateTo(sectionIndex, subsectionIndex)}
                  >
                    <div
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        sectionIndex === currentSectionIndex &&
                          subsectionIndex === currentSubsectionIndex
                          ? 'bg-primary'
                          : 'bg-primary/5',
                      )}
                    />
                    <span className="truncate">{subsection.title || 'Untitled Subsection'}</span>
                  </div>
                ))}
                <div
                  role="button"
                  className="text-muted-foreground hover:bg-muted hover:text-foreground flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm transition-colors"
                  onClick={event => {
                    event.stopPropagation();
                    const newSubsection = createNewSubsection();
                    addSubsection(section.id, newSubsection);
                    navigateTo(sectionIndex, section.subsections.length);
                  }}
                >
                  <Plus className="size-3.5 shrink-0" />
                  <span>Add Subsection</span>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>

        <div className="mt-4 border-t pt-4">
          <div className="w-full">
            <Button
              variant="outline"
              onClick={() => addSection(createNewSection())}
              className="w-full"
            >
              <Plus />
              Add Section
            </Button>
          </div>
          <p className="text-muted-foreground mt-4 text-center text-xs">
            {formDefinition.sections.length} sections,{' '}
            {Object.keys(formDefinition.questions).length} questions
          </p>
        </div>
      </div>
    </div>
  );
}
